import { Injectable, NgZone } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Subscription } from 'rxjs';

/**
 * A screen's claim on the hardware back button. Return true to CONSUME the press
 * (the screen handled it itself); return false to let it fall through to normal
 * navigation.
 */
export type BackHandler = () => boolean;

/**
 * What a back press should do, given the handler stack. Handlers are consulted
 * LIFO — the most recently registered screen wins — which is what makes a nested
 * state (a guest's detail card inside the scanner) able to claim the press ahead
 * of anything registered earlier.
 *
 * Pure and exported so the decision is unit-testable without Capacitor.
 */
export function resolveBackAction(
  handlers: readonly BackHandler[],
  canGoBack: boolean
): 'consumed' | 'back' | 'exit' {
  for (let i = handlers.length - 1; i >= 0; i--) {
    if (handlers[i]()) {
      return 'consumed';
    }
  }
  return canGoBack ? 'back' : 'exit';
}

/**
 * Contextual hardware-back handling for Android.
 *
 * WHY THIS EXISTS: nothing handled the back button, so @capacitor/app fell through
 * to its default — a raw `webView.goBack()`. That is wrong here for two reasons.
 *
 * 1. The guest DETAIL and the CAMERA are not routes. They are `mode$` states inside
 *    one ScannerComponent living at a single URL, so they push no history entry.
 *    A raw history pop from any of them therefore skipped the whole screen and
 *    landed on group selection — back from a guest card should return to the list.
 *
 * 2. When it cannot go back the default does NOTHING, so the app could not be
 *    dismissed with back from the home screen.
 *
 * ONE LISTENER, APP-WIDE, ON PURPOSE: `hasListeners("backButton")` is a global
 * check inside the plugin — the moment ANY listener exists, Capacitor stops doing
 * its default for EVERY back press. So registering per-screen would silently
 * disable normal back navigation on every other screen. This service owns the
 * single listener and re-implements the default (`back` / `exit`) for the cases no
 * screen claims.
 */
@Injectable({ providedIn: 'root' })
export class BackButtonService {
  private readonly handlers: BackHandler[] = [];

  constructor(private zone: NgZone) {
    if (!Capacitor.isNativePlatform()) {
      return; // browsers have their own back button; don't fight it
    }
    // Capacitor fires this outside Angular's zone — run inside, or a handler that
    // flips a BehaviorSubject updates no view until something else triggers CD.
    App.addListener('backButton', ({ canGoBack }) =>
      this.zone.run(() => {
        switch (resolveBackAction(this.handlers, canGoBack)) {
          case 'consumed':
            return;
          case 'back':
            window.history.back();
            return;
          case 'exit':
            App.exitApp();
        }
      })
    );
  }

  /**
   * Claim the back button while a screen is alive. Returns a Subscription so the
   * caller can hand it straight to BaseRxjsComponent's `sub` setter and have it
   * released on destroy — leaving a handler registered would let a dead screen keep
   * swallowing back presses.
   */
  register(handler: BackHandler): Subscription {
    this.handlers.push(handler);
    return new Subscription(() => {
      const i = this.handlers.indexOf(handler);
      if (i >= 0) {
        this.handlers.splice(i, 1);
      }
    });
  }
}
